import { validMsaDate } from './msaAgreements';
import type { CoverageInput, CoverageRequirement } from './msaCoverage';

export class QualificationValidationError extends Error {}
export const INSURANCE_TYPES = {
  general_liability: 'General liability',
  auto_liability: 'Commercial auto liability',
  workers_comp: 'Workers’ compensation',
  employers_liability: 'Employers’ liability',
  umbrella: 'Umbrella / excess',
  pollution: 'Pollution liability',
  professional: 'Professional liability',
} as const;
export const QUALIFICATION_KINDS = {
  insurance: 'Insurance',
  license: 'License',
  training: 'Training',
  safety: 'Safety document',
} as const;
export const QUALIFICATION_DOCUMENT_KINDS = {
  certificate: 'Certificate',
  policy: 'Policy',
  endorsement: 'Endorsement',
  license: 'License',
  training: 'Training',
  safety: 'Safety document',
} as const;
export type InsuranceTerms = {
  coverageType: keyof typeof INSURANCE_TYPES;
  perOccurrence: number | null;
  aggregate: number | null;
  endorsements: string[];
};
export type QualificationDetails = {
  kind: keyof typeof QUALIFICATION_KINDS;
  title: string;
  insuredName: string;
  insurer: string;
  policyNumber: string;
  brokerName: string;
  brokerEmail: string;
  effectiveDate: string;
  expiryDate: string;
  insurance: InsuranceTerms | null;
};
export type QualificationDocument = {
  id: string;
  name: string;
  kind: keyof typeof QUALIFICATION_DOCUMENT_KINDS;
  sha256: string;
  size: number;
  mime: string;
  createdAt: string;
};
export type QualificationFile = {
  id: string;
  companyId: string;
  companyName: string;
  version: number;
  status: 'active' | 'withdrawn' | 'replaced';
  replacementId: string;
  details: QualificationDetails;
  documents: QualificationDocument[];
  shares: string[];
  updatedAt: string;
  updatedBy: string;
};
export type QualificationWorkspace = {
  companyId: string;
  companyName: string;
  owner: boolean;
  files: QualificationFile[];
  received: QualificationFile[];
  connections: { id: string; name: string }[];
  operatorRequirements: {
    companyId: string;
    companyName: string;
    version: number;
    requirements: CoverageRequirement[];
  }[];
};
export const emptyInsuranceTerms = (): InsuranceTerms => ({
  coverageType: 'general_liability',
  perOccurrence: null,
  aggregate: null,
  endorsements: [],
});
export const emptyQualificationDetails = (
  companyName = '',
): QualificationDetails => ({
  kind: 'insurance',
  title: '',
  insuredName: companyName,
  insurer: '',
  policyNumber: '',
  brokerName: '',
  brokerEmail: '',
  effectiveDate: '',
  expiryDate: '',
  insurance: emptyInsuranceTerms(),
});
function text(v: unknown, limit = 300) {
  if (typeof v !== 'string' || v.length > limit || /[\u0000-\u001f]/.test(v))
    throw new QualificationValidationError('Invalid qualification field.');
  return v.trim();
}
export function normalizeInsuranceTerms(value: unknown): InsuranceTerms {
  const v = value as InsuranceTerms;
  if (
    !v ||
    !Object.prototype.hasOwnProperty.call(INSURANCE_TYPES, v.coverageType) ||
    !Array.isArray(v.endorsements) ||
    v.endorsements.length > 30
  )
    throw new QualificationValidationError(
      'Choose the insurance coverage and required endorsements.',
    );
  const amount = (a: unknown) => {
    if (a === null) return null;
    if (
      typeof a !== 'number' ||
      !Number.isSafeInteger(a) ||
      a < 0 ||
      a > 1_000_000_000
    )
      throw new QualificationValidationError(
        'Coverage limits must be whole US dollar amounts.',
      );
    return a;
  };
  return {
    coverageType: v.coverageType,
    perOccurrence: amount(v.perOccurrence),
    aggregate: amount(v.aggregate),
    endorsements: [
      ...new Set(v.endorsements.map((e) => text(e, 180)).filter(Boolean)),
    ],
  };
}
export function normalizeQualificationDetails(
  value: unknown,
): QualificationDetails {
  const v = value as QualificationDetails;
  if (!v || !Object.prototype.hasOwnProperty.call(QUALIFICATION_KINDS, v.kind))
    throw new QualificationValidationError('Choose the qualification type.');
  const result: QualificationDetails = {
    kind: v.kind,
    title: text(v.title),
    insuredName: text(v.insuredName),
    insurer: text(v.insurer),
    policyNumber: text(v.policyNumber),
    brokerName: text(v.brokerName),
    brokerEmail: text(v.brokerEmail),
    effectiveDate: text(v.effectiveDate),
    expiryDate: text(v.expiryDate),
    insurance:
      v.kind === 'insurance' ? normalizeInsuranceTerms(v.insurance) : null,
  };
  if (
    !result.title ||
    !result.insuredName ||
    !validMsaDate(result.effectiveDate) ||
    !validMsaDate(result.expiryDate) ||
    result.expiryDate < result.effectiveDate
  )
    throw new QualificationValidationError(
      'Provide a title, legal entity, and valid effective and expiration dates.',
    );
  if (
    v.kind === 'insurance' &&
    (!result.insurer ||
      !result.policyNumber ||
      !result.brokerName ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(result.brokerEmail))
  )
    throw new QualificationValidationError(
      'Provide the insurer, policy number, broker name, and broker email.',
    );
  return result;
}
const normalized = (value: string) =>
  value
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]/gu, '');
export function qualificationProblems(
  requirement: CoverageRequirement,
  file: QualificationFile | undefined,
  work: CoverageInput,
  today: string,
): string[] {
  if (!file)
    return ['A current contractor file shared with this operator is required.'];
  const problems: string[] = [];
  const d = file.details;
  if (file.companyId !== work.contractorCompanyId)
    problems.push('The file belongs to a different contractor.');
  if (file.status !== 'active')
    problems.push(
      'This file was withdrawn or replaced; select current evidence.',
    );
  if (normalized(d.insuredName) !== normalized(file.companyName))
    problems.push(
      'The insured legal entity differs from the registered contractor.',
    );
  if (d.effectiveDate > work.startDate)
    problems.push('Coverage starts after the work begins.');
  if (d.expiryDate < today || d.expiryDate < work.endDate)
    problems.push('Evidence has expired or expires before this work ends.');
  if (!file.documents.length)
    problems.push('Upload original supporting documents.');
  if (requirement.insurance) {
    const terms = requirement.insurance;
    if (
      d.kind !== 'insurance' ||
      d.insurance?.coverageType !== terms.coverageType
    )
      problems.push('The insurance coverage type does not match.');
    for (const key of ['perOccurrence', 'aggregate'] as const) {
      if (
        terms[key] !== null &&
        (d.insurance?.[key] == null || d.insurance[key]! < terms[key]!)
      )
        problems.push(
          `${key === 'perOccurrence' ? 'Per occurrence' : 'Aggregate'} limit is below the operator’s requirement.`,
        );
    }
    const missing = terms.endorsements.filter(
      (e) =>
        !d.insurance?.endorsements.some(
          (got) => normalized(got) === normalized(e),
        ),
    );
    if (missing.length)
      problems.push(`Missing endorsements: ${missing.join(', ')}.`);
    if (!file.documents.some((doc) => doc.kind === 'policy'))
      problems.push(
        'The relevant policy document is required; a certificate alone is insufficient.',
      );
    if (
      terms.endorsements.length &&
      !file.documents.some((doc) => doc.kind === 'endorsement')
    )
      problems.push('Upload the required endorsement documents.');
  } else if (
    requirement.qualificationKind &&
    (d.kind !== requirement.qualificationKind ||
      !file.documents.some((doc) => doc.kind === requirement.qualificationKind))
  )
    problems.push('Supply the required license, training, or safety document.');
  return problems;
}
export function renewalStatus(
  file: QualificationFile,
  today: string,
  days = 30,
): string | null {
  if (file.status !== 'active') return null;
  const remaining = Math.ceil(
    (Date.parse(`${file.details.expiryDate}T00:00:00Z`) -
      Date.parse(`${today}T00:00:00Z`)) /
      86400000,
  );
  return remaining < 0
    ? 'Expired — renewal and review required'
    : remaining <= days
      ? `Renewal due in ${remaining} day${remaining === 1 ? '' : 's'} — renewed documents need review`
      : null;
}
